import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowRight, 
  Calendar, 
  Users, 
  Shield, 
  Star, 
  Phone, 
  Video, 
  Building2, 
  Stethoscope,
  Clock,
  CheckCircle,
  Heart,
  Brain,
  Activity,
  Award,
  MapPin,
  Trash2,
  AlertTriangle,
  Eye,
  EyeOff,
  Home,
  Monitor
} from 'lucide-react';
import { ServicesShowcase } from './ServicesShowcase';
import { InsuranceInfo } from './InsuranceInfo';

export const HomePage = () => {
  const { user, clearAllData } = useAuth();
  // const user = null
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    // Collect debug information
    const localStorageData = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        localStorageData[key] = localStorage.getItem(key);
      }
    }

    setDebugInfo({
      currentUser: user,
      localStorage: localStorageData,
      sessionStorage: { ...sessionStorage },
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  }, [user]);

  const handleEmergencyClear = () => {
    // if (confirm('🚨 EMERGENCY CLEAR: This will clear ALL data and reload the page. Are you sure?')) {
    //   console.log('🚨 EMERGENCY CLEAR initiated');
    //   clearAllData();
    //   window.location.reload();
    // }
  };

  // If user is logged in, show their dashboard link prominently
  if (user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50">
        {/* Debug Panel */}
        <div className="bg-yellow-50 border-b-2 border-yellow-200 p-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="text-yellow-800 font-medium">
                  Debug Mode: You are logged in as <strong>{user.role}</strong> ({user.name})
                </span>
                <Badge className="bg-yellow-200 text-yellow-800">
                  {user.role.toUpperCase()}
                </Badge>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDebug(!showDebug)}
                  className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                >
                  {showDebug ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {showDebug ? 'Hide' : 'Show'} Debug Info
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEmergencyClear}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Emergency Clear
                </Button>
              </div>
            </div>

            {showDebug && (
              <div className="mt-4 p-4 bg-white rounded-lg border">
                <h3 className="font-semibold text-gray-900 mb-3">Debug Information:</h3>
                <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-64">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-8">
              <div className="icon-container icon-container-2xl icon-medical mx-auto mb-6">
                {user.role === 'patient' && <Heart className="icon-3xl" />}
                {user.role === 'provider' && <Stethoscope className="icon-3xl" />}
                {user.role === 'facility' && <Building2 className="icon-3xl" />}
                {user.role === 'admin' && <Shield className="icon-3xl" />}
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-clinic-gray-900 mb-4">
                Welcome back, {user.name}!
              </h1>
              <p className="text-xl text-clinic-gray-600 mb-8">
                {user.role === 'patient' && 'Access your health portal, schedule appointments, and manage your care.'}
                {user.role === 'provider' && 'Manage your practice, review referrals, and coordinate patient care.'}
                {user.role === 'facility' && 'Submit referrals, track care requests, and manage facility operations.'}
                {user.role === 'admin' && 'Monitor platform operations, manage users, and oversee system performance.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button asChild size="lg" className="btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700">
                <Link to={`/${user.role}/dashboard`}>
                  <Activity className="w-5 h-5 mr-2" />
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              
              {user.role === 'patient' && (
                <Button asChild variant="outline" size="lg" className="btn-lg">
                  <Link to="/booking/telehealth">
                    <Video className="w-5 h-5 mr-2" />
                    Book Appointment
                  </Link>
                </Button>
              )}
              
              {user.role === 'facility' && (
                <Button asChild variant="outline" size="lg" className="btn-lg">
                  <Link to="/booking/onsite">
                    <Calendar className="w-5 h-5 mr-2" />
                    Request Visit
                  </Link>
                </Button>
              )}
            </div>

            {/* Quick Stats for logged in users */}
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <div className="icon-container icon-container-lg icon-success mx-auto mb-4">
                    <CheckCircle className="icon-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-clinic-gray-900 mb-2">Account Active</h3>
                  <p className="text-clinic-gray-600">Your {user.role} account is fully activated and ready to use.</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <div className="icon-container icon-container-lg icon-medical mx-auto mb-4">
                    <Shield className="icon-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-clinic-gray-900 mb-2">HIPAA Compliant</h3>
                  <p className="text-clinic-gray-600">All data is encrypted and stored securely according to healthcare standards.</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardContent className="p-6 text-center">
                  <div className="icon-container icon-container-lg icon-success mx-auto mb-4">
                    <Clock className="icon-xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-clinic-gray-900 mb-2">24/7 Support</h3>
                  <p className="text-clinic-gray-600">Round-the-clock technical support and emergency assistance available.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <ServicesShowcase />
        <InsuranceInfo />
      </div>
    );
  }

  // Default homepage for non-logged-in users
  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50">
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold text-clinic-gray-900 mb-6 leading-tight">
                  Comprehensive Psychiatric Care 
                  <span className="text-clinic-blue-600"> Everywhere You Need It</span>
                </h1>
                <p className="text-xl text-clinic-gray-600 mb-8 leading-relaxed">
                  EnnVital provides expert psychiatric services to individual patients at home through secure telehealth 
                  and to residents in healthcare facilities through onsite visits. Quality mental health care, delivered where you are.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Button asChild size="lg" className="btn-lg bg-clinic-blue-600 hover:bg-clinic-blue-700 shadow-lg hover:shadow-xl transition-all duration-200">
                  <Link to="/for-patients"
                  className='no-underline'
                  >
                    <Home className="w-5 h-5 mr-2" />
                    Patients at Home
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button asChild size="lg" className="btn-lg bg-clinic-gold-600 hover:bg-clinic-gold-700 shadow-lg hover:shadow-xl transition-all duration-200 text-white">
                  <Link to="/for-facilities"
                   className='no-underline'
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    Healthcare Facilities
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              <div className="flex items-center space-x-6 text-sm text-clinic-gray-500">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-clinic-blue-600" />
                  <span>HIPAA Compliant</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="w-4 h-4 text-clinic-gold-600" />
                  <span>Licensed Providers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-clinic-success-600" />
                  <span>24/7 Emergency Support</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-clinic-blue-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-clinic-gray-900">Choose Your Care Type</h3>
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-clinic-blue-50 rounded-lg border border-clinic-blue-200">
                      <div className="flex items-center mb-3">
                        <Video className="w-5 h-5 text-clinic-blue-600 mr-3" />
                        <h4 className="font-semibold text-clinic-blue-900">Individual Patients</h4>
                      </div>
                      <p className="text-sm text-clinic-blue-700 mb-3">Secure telehealth appointments from the comfort of your home</p>
                      <Button asChild size="sm" className="w-full bg-clinic-blue-600 hover:bg-clinic-blue-700 no-underline">
                        <Link to="/for-patients">
                          Get Started
                        </Link>
                      </Button>
                    </div>

                    <div className="p-4 bg-clinic-gold-50 rounded-lg border border-clinic-gold-200">
                      <div className="flex items-center mb-3">
                        <Building2 className="w-5 h-5 text-clinic-gold-600 mr-3" />
                        <h4 className="font-semibold text-clinic-gold-900">Healthcare Facilities</h4>
                      </div>
                      <p className="text-sm text-clinic-gold-700 mb-3">Onsite psychiatric visits for nursing homes and care facilities</p>
                      <Button asChild size="sm" className="w-full bg-clinic-gold-600 hover:bg-clinic-gold-700 text-white no-underline">
                        <Link to="/for-facilities">
                          Learn More
                        </Link>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-clinic-gray-200">
                    <p className="text-xs text-clinic-gray-500 text-center">
                      Emergency? Call <span className="font-semibold text-clinic-blue-600">(555) 123-4567</span>
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-clinic-gold-200 rounded-full opacity-60 animate-float"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-clinic-blue-200 rounded-full opacity-40 animate-float" style={{ animationDelay: '-2s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Dual Service Showcase */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-clinic-gray-900 mb-4">
              Two Ways to Receive Care
            </h2>
            <p className="text-lg text-clinic-gray-600 max-w-3xl mx-auto">
              Whether you're an individual seeking care at home or a healthcare facility needing onsite psychiatric services, 
              EnnVital has the solution that works for you.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Telehealth for Individuals */}
           <Card className="card-elevated custom-gradient-card">
              <CardContent className="p-8">
                <div className="icon-container icon-container-xl icon-medical mx-auto mb-6">
                  <Monitor className="icon-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-clinic-blue-900 mb-4 text-center">Telehealth at Home</h3>
                <p className="text-clinic-blue-700 mb-6 text-center">
                  Convenient, secure video consultations with licensed psychiatric providers from your own home.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-clinic-blue-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-blue-600" />
                    Individual therapy sessions
                  </li>
                  <li className="flex items-center text-clinic-blue-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-blue-600" />
                    Medication management
                  </li>
                  <li className="flex items-center text-clinic-blue-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-blue-600" />
                    Crisis intervention support
                  </li>
                  <li className="flex items-center text-clinic-blue-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-blue-600" />
                    Family counseling sessions
                  </li>
                </ul>
        <Button asChild className="btn-full-clinic-blue">
  <Link to="/for-patients" className="btn-icon-spacing">
    <Home className="icon" />
    Explore Patient Services
  </Link>
</Button>


              </CardContent>
            </Card>

            {/* Onsite for Facilities */}
           <Card className="card-elevated-clinic-gold card-gradient-clinic-gold">
              <CardContent className="p-8">
                <div className="icon-container icon-container-xl icon-warning mx-auto mb-6">
                  <Building2 className="icon-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-clinic-gold-900 mb-4 text-center">Onsite Facility Visits</h3>
                <p className="text-clinic-gold-700 mb-6 text-center">
                  Expert psychiatric providers visit your facility to provide comprehensive care to residents.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-clinic-gold-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-gold-600" />
                    Nursing home consultations
                  </li>
                  <li className="flex items-center text-clinic-gold-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-gold-600" />
                    Assisted living support
                  </li>
                  <li className="flex items-center text-clinic-gold-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-gold-600" />
                    Rehabilitation center services
                  </li>
                  <li className="flex items-center text-clinic-gold-800">
                    <CheckCircle className="w-4 h-4 mr-3 text-clinic-gold-600" />
                    Staff training and support
                  </li>
                </ul>
              <Button asChild className="btn-full-clinic-gold">
  <Link to="/for-facilities" className="btn-icon-spacing-gold">
    <Building2 className="icon" />
    Explore Facility Services
  </Link>
</Button>

              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-clinic-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-clinic-gray-900 mb-4">
              Trusted Across All Care Settings
            </h2>
            <p className="text-lg text-clinic-gray-600 max-w-3xl mx-auto">
              Providing exceptional psychiatric care to individuals at home and residents in healthcare facilities nationwide.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-clinic-blue-600 mb-2">10K+</div>
              <div className="text-clinic-gray-600">Individual Patients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-clinic-gold-600 mb-2">500+</div>
              <div className="text-clinic-gray-600">Partner Facilities</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-clinic-blue-600 mb-2">50+</div>
              <div className="text-clinic-gray-600">Licensed Providers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-clinic-blue-600 mb-2">4.9★</div>
              <div className="text-clinic-gray-600">Patient Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Showcase */}
      <ServicesShowcase />
      
      {/* Insurance Information */}
      <InsuranceInfo />

      {/* CTA Section */}
    <section className="clinic-section-gradient">
  <div className="clinic-container-centered">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Experience Quality Psychiatric Care?
          </h2>
          <p className="text-xl text-clinic-blue-100 mb-8 leading-relaxed">
            Whether you're seeking individual care at home or facility-based services, 
            EnnVital connects you with expert psychiatric providers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="btn-lg bg-white text-clinic-blue-600 hover:bg-clinic-gray-50">
              <Link to="/for-patients" className='no-underline'>
                <Home className="w-5 h-5 mr-2" />
                Individual Care
              </Link>
            </Button>
            <Button asChild size="lg" className="btn-lg bg-clinic-gold-600 hover:bg-clinic-gold-700 text-white border-2 border-clinic-gold-600">
              <Link to="/for-facilities" className='no-underline'>
                <Building2 className="w-5 h-5 mr-2" />
                Facility Services
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};